/**
 * Minimal markdown renderer for comment bodies. Handles:
 *
 *   **bold**     *italic*     `code`
 *   ```fenced code blocks```
 *   - bullet  or  * bullet
 *   1. numbered
 *   [text](url)
 *   line breaks (\n → <br/>)
 *
 * Safe against XSS — we only construct React elements, never HTML strings,
 * and we escape URLs in links. We deliberately do NOT support headings,
 * tables, or images; comments don't need them and the parser stays tiny.
 *
 * If you ever need full Markdown, drop in `react-markdown`. The shape of
 * this component is compatible.
 */

import React from 'react';

interface Props {
  source: string;
}

export default function Markdown({ source }: Props) {
  if (!source) return null;
  return <div className="markdown text-sm leading-relaxed">{renderBlocks(source)}</div>;
}

function renderBlocks(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  // First isolate fenced code blocks so we don't apply inline rules inside them.
  const segments = splitFenced(text);
  segments.forEach((seg, i) => {
    if (seg.type === 'code') {
      parts.push(
        <pre
          key={`c${i}`}
          className="bg-slate-100 border border-slate-200 rounded p-2 my-1 font-mono text-xs whitespace-pre overflow-x-auto"
        >
          {seg.text}
        </pre>,
      );
    } else {
      parts.push(...renderProseBlock(seg.text, `p${i}`));
    }
  });
  return parts;
}

function splitFenced(
  text: string,
): Array<{ type: 'code' | 'prose'; text: string }> {
  const out: Array<{ type: 'code' | 'prose'; text: string }> = [];
  const re = /```([\s\S]*?)```/g;
  let lastIdx = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIdx) {
      out.push({ type: 'prose', text: text.slice(lastIdx, m.index) });
    }
    out.push({ type: 'code', text: (m[1] ?? '').replace(/^\n/, '').replace(/\n$/, '') });
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) {
    out.push({ type: 'prose', text: text.slice(lastIdx) });
  }
  return out;
}

function renderProseBlock(text: string, keyBase: string): React.ReactNode[] {
  // Group lines into list blocks vs paragraphs.
  const lines = text.split('\n');
  const blocks: React.ReactNode[] = [];
  let buf: string[] = [];
  let listKind: 'ul' | 'ol' | null = null;
  let listItems: string[] = [];
  let idx = 0;

  const flushParagraph = () => {
    if (buf.length === 0) return;
    blocks.push(
      <p key={`${keyBase}-${idx++}`}>
        {buf.map((l, i) => (
          <React.Fragment key={i}>
            {renderInline(l)}
            {i < buf.length - 1 ? <br /> : null}
          </React.Fragment>
        ))}
      </p>,
    );
    buf = [];
  };
  const flushList = () => {
    if (listKind === null) return;
    const Tag = listKind;
    blocks.push(
      <Tag
        key={`${keyBase}-${idx++}`}
        className={listKind === 'ul' ? 'list-disc pl-5' : 'list-decimal pl-5'}
      >
        {listItems.map((item, i) => (
          <li key={i}>{renderInline(item)}</li>
        ))}
      </Tag>,
    );
    listKind = null;
    listItems = [];
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const ulMatch = /^[\-*]\s+(.*)$/.exec(line);
    const olMatch = /^\d+\.\s+(.*)$/.exec(line);
    if (ulMatch) {
      flushParagraph();
      if (listKind !== 'ul') flushList();
      listKind = 'ul';
      listItems.push(ulMatch[1] ?? '');
      continue;
    }
    if (olMatch) {
      flushParagraph();
      if (listKind !== 'ol') flushList();
      listKind = 'ol';
      listItems.push(olMatch[1] ?? '');
      continue;
    }
    if (line.trim() === '') {
      flushParagraph();
      flushList();
      continue;
    }
    if (listKind !== null) flushList();
    buf.push(line);
  }
  flushParagraph();
  flushList();
  return blocks;
}

/**
 * Inline formatting: bold, italic, code, links. Order matters because
 * we process by recursive replacement on plain-text segments only.
 */
function renderInline(text: string): React.ReactNode {
  // Tokenize the line into segments split by markdown markers.
  // We use a regex-by-precedence approach: code first (so its contents
  // aren't re-formatted), then links, then bold, then italic.
  const tokens: React.ReactNode[] = [];
  let i = 0;
  let remaining = text;
  while (remaining.length > 0) {
    const match = nextInlineMatch(remaining);
    if (!match) {
      tokens.push(remaining);
      break;
    }
    if (match.start > 0) {
      tokens.push(remaining.slice(0, match.start));
    }
    tokens.push(match.node(i++));
    remaining = remaining.slice(match.end);
  }
  return tokens;
}

function nextInlineMatch(
  s: string,
): { start: number; end: number; node: (key: number) => React.ReactNode } | null {
  const patterns: Array<{
    re: RegExp;
    wrap: (groups: RegExpExecArray) => (key: number) => React.ReactNode;
  }> = [
    {
      re: /`([^`\n]+)`/, // inline code
      wrap: (m) => (key) => (
        <code key={key} className="bg-slate-100 border border-slate-200 rounded px-1 font-mono text-[12px]">
          {m[1]}
        </code>
      ),
    },
    {
      re: /\[([^\]]+)\]\(([^)\s]+)\)/, // link
      wrap: (m) => (key) => {
        const url = m[2]!;
        if (!/^(https?:\/\/|\/|mailto:)/.test(url)) {
          return <span key={key}>{m[1]}</span>;
        }
        return (
          <a key={key} href={url} className="text-emerald-700 underline" target="_blank" rel="noreferrer">
            {m[1]}
          </a>
        );
      },
    },
    {
      re: /\*\*([^*\n]+)\*\*/, // bold
      wrap: (m) => (key) => <strong key={key}>{m[1]}</strong>,
    },
    {
      re: /\*([^*\n]+)\*/, // italic
      wrap: (m) => (key) => <em key={key}>{m[1]}</em>,
    },
  ];
  let earliest: {
    start: number;
    end: number;
    node: (key: number) => React.ReactNode;
  } | null = null;
  for (const p of patterns) {
    const m = p.re.exec(s);
    if (m && m.index >= 0) {
      if (!earliest || m.index < earliest.start) {
        earliest = {
          start: m.index,
          end: m.index + m[0].length,
          node: p.wrap(m),
        };
      }
    }
  }
  return earliest;
}
